import React, { createContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    createUser,
    getUser,
    getUserByRepoPartnerId,
} from '../../redux/api/chatAPI/user';
import { socketIO } from '../../helpers/connectionSocket.helper';
import { userAdapter } from '../../pages/chatV2/adapters';
import { deleteNotificationRequest } from '../../redux/api/chat-V2/http/notification';
import GenericSnackbar from '../../components/common/GenericSnackbar';

export const ChatContext = createContext([]);

export const ChatProvider = ({ children }) => {
    const email = useSelector((state) => state?.auth?.user?.personal_email);
    const userData = useSelector((state) => state?.auth?.user);

    const history = useHistory();
    const pageRef = useRef(1);
    const attemptsRef = useRef(0);

    const [user, setUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [rooms, setRooms] = useState(null);
    const [roomMessages, setRoomMessages] = useState(null);
    const [currentRoom, setCurrentRoom] = useState(null);
    const [status, setStatus] = useState(null);
    const [socketId, setSocketId] = useState(null);
    const [latestMessage, setLatestMessage] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [reciverConnection, setReciverConnection] = useState(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);

    useEffect(() => {
        if (email && userData) {
            getChatUser(email);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, userData]);

    useEffect(() => {
        socketIO.connect();

        // Clean socket on unmount
        return () => {
            socketIO.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!user?.id) return;

        // Set user as online
        socketIO.emit('setUserOnline', { user_id: user.id });
        socketIO.emit('setUserStatus', { user_id: user.id, status: true });

        const handleUserConnected = (isConnected) => {
            setStatus(isConnected);
        };

        const handleUserDisconnected = (isConnected) => {
            setStatus(isConnected);
        };

        const handleNotifications = (notifications) => {
            setNotifications(notifications);
        };

        socketIO.on('userConnected', handleUserConnected);
        socketIO.on('userDisconnected', handleUserDisconnected);
        socketIO.on('notification', handleNotifications);

        // Get initial notifications
        getNotifications(user.id);

        return () => {
            socketIO.off('userConnected', handleUserConnected);
            socketIO.off('userDisconnected', handleUserDisconnected);
            socketIO.off('notification', handleNotifications);
        };
    }, [user?.id]);

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const getRoomMessages = (roomId) => {
        socketIO.emit('getMessages', { room_id: roomId });
    };

    const checkUserStatus = (userId) => {
        socketIO.emit('getConnectionStatus', userId);
        socketIO.once('getConnectionStatus', (status) => {
            setStatus(status);
        });
    };

    const setMessagesAsSeen = (messages, reciverSocketId) => {
        socketIO.emit('setMessagesAsSeen', {
            messages,
            socket_id: reciverSocketId,
        });
    };

    const getNotifications = (reciver_id) => {
        socketIO.emit('getNotifications', { reciver: reciver_id });
        socketIO.once('getNotifications', (notifications) => {
            setNotifications(notifications);
        });
    };

    const createRoom = (participant) => {
        const payload = {
            created_by: user?.id,
            participants: [
                { user_id: user?.id, name: user?.name },
                {
                    user_id: participant?.id ?? participant?._id,
                    name: participant?.name,
                },
            ],
        };

        socketIO.emit('create-room', payload);

        socketIO.once('create-room', (room) => {
            const roomId = Array.isArray(room) ? room[0]?._id : room?._id;
            if (roomId) history.push(`/chat?room=${roomId}`);
        });
    };

    const getChatUser = async (email) => {
        try {
            attemptsRef.current += 1;
            const userResponse = await getUser({ email });

            if (userResponse?.statusCode === 404 && attemptsRef.current < 3) {
                const created = await createChatUser();
                if (created) getChatUser(email);
                return;
            }

            const adapted = userAdapter(userResponse);
            setUser((prev) =>
                JSON.stringify(prev) !== JSON.stringify(adapted)
                    ? adapted
                    : prev
            );
        } catch (error) {
            console.error('Error fetching chat user:', error);
        }
    };

    const createChatUser = async () => {
        try {
            const payload = {
                name: userData?.name,
                email: userData?.personal_email,
                origin: 'acropolis',
                online: true,
                profile_photo: userData?.foto,
                repo_partner_id: userData?.repo_id,
                roles: userData?.roles,
            };

            const newUser = await createUser(payload);
            if (newUser.statusCode >= 400) {
                console.error('Error creating chat user:', newUser);
                return false;
            }

            return true;
        } catch (err) {
            console.error('Error in createChatUser:', err);
            return false;
        }
    };

    const deleteNotification = async (notificationId) => {
        try {
            const request = await deleteNotificationRequest(notificationId);
            if (request.error) return false;

            setNotifications((prev) =>
                prev.filter((n) => n._id !== notificationId)
            );
            return true;
        } catch {
            return false;
        }
    };

    const handleChat = async (partnerId) => {
        const user = await getUserByRepoPartnerId(partnerId);

        if (user.statusCode === 404) {
            setOpenSnackbar(true);
            return;
        }

        createRoom(user);
    };

    const values = {
        user,
        rooms,
        status,
        pageRef,
        messages,
        socketId,
        currentRoom,
        roomMessages,
        latestMessage,
        notifications,
        reciverConnection,
        setReciverConnection,
        deleteNotification,
        setMessagesAsSeen,
        setLatestMessage,
        getNotifications,
        setOpenSnackbar,
        getRoomMessages,
        checkUserStatus,
        setRoomMessages,
        setCurrentRoom,
        setMessages,
        setSocketId,
        handleChat,
        createRoom,
        setRooms,
    };

    return (
        <ChatContext.Provider value={values}>
            <GenericSnackbar
                open={openSnackbar}
                vertical="top"
                severity="error"
                message="No se pudo crear el chat, contacte con su asesor académico"
                handleClose={handleCloseSnackbar}
            />
            {children}
        </ChatContext.Provider>
    );
};
